import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, HelpCircle, Boxes, MessageSquareQuote, FileText, Settings, LogOut, ExternalLink, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/site/Logo";
import LeadsPanel from "../../components/admin/LeadsPanel";
import ContentPanel from "../../components/admin/ContentPanel";
import SettingsPanel from "../../components/admin/SettingsPanel";

const NAV = [
  { key: "leads", label: "Leads", icon: Inbox },
  { key: "faqs", label: "FAQs", icon: HelpCircle },
  { key: "products", label: "Products", icon: Boxes },
  { key: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { key: "case_studies", label: "Case Studies", icon: FileText },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("leads");
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const renderPanel = () => {
    if (active === "leads") return <LeadsPanel />;
    if (active === "settings") return <SettingsPanel />;
    return <ContentPanel collection={active} key={active} />;
  };

  const Sidebar = (
    <aside className="w-64 shrink-0 border-r border-white/5 bg-surface-1 flex flex-col h-full">
      <div className="p-6 border-b border-white/5">
        <Logo size={26} />
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand">Admin</div>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {NAV.map((n) => {
          const Icon = n.icon;
          return (
            <button
              key={n.key}
              onClick={() => { setActive(n.key); setMenuOpen(false); }}
              data-testid={`nav-${n.key}`}
              className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors ${active === n.key ? "bg-brand/15 text-white border border-brand/30" : "text-muted-foreground hover:text-white hover:bg-white/5 border border-transparent"}`}
            >
              <Icon className="h-4 w-4" /> {n.label}
            </button>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/5 space-y-1">
        <a href="/" target="_blank" rel="noreferrer" className="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors">
          <ExternalLink className="h-4 w-4" /> View site
        </a>
        <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm text-muted-foreground hover:text-red-400 hover:bg-white/5 transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="dark min-h-screen bg-void text-foreground font-body flex" data-testid="admin-dashboard">
      <div className="hidden md:block h-screen sticky top-0">{Sidebar}</div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="h-full">{Sidebar}</div>
          <div className="flex-1 bg-black/60" onClick={() => setMenuOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <header className="md:hidden flex items-center justify-between p-4 border-b border-white/5 bg-surface-1 sticky top-0 z-40">
          <Logo size={24} />
          <button onClick={() => setMenuOpen((v) => !v)} className="p-2 rounded-lg bg-white/5 border border-white/10 text-white">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </header>

        <div className="p-6 md:p-10 max-w-5xl">
          <div className="flex items-center justify-between mb-8">
            <div className="text-sm text-muted-foreground">
              Signed in as <span className="text-white">{user?.email}</span>
            </div>
          </div>
          {renderPanel()}
        </div>
      </div>
    </div>
  );
}
